import { Box, Typography } from '@mui/material';
import { FC, useCallback, useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation } from 'react-router-dom';
import queryString from 'query-string';
import { ESortDirection, TTableStructure, TTableStructureFilter } from 'ui/EasTable/types';
import { EasTableWithFilter } from 'ui/EasTableWithFilter';
import { getColumnFilterValue } from 'utils/FilterParams';
import { PAGE_SIZE } from 'services/constants/env';
import { TFilters, TOrderBy } from 'redux/types';
import { getAnnualExchangeRateListRequest } from 'redux/reducers/exchangeRates/reducer';
import {
	getAnnualExchangeRateList,
	getAnnualExchangeRateListLoading,
} from 'redux/reducers/exchangeRates/selectors';
import { getEmEnumsRequest } from 'redux/reducers/emManagement/reducer';
import { EEmEnums } from 'redux/reducers/emManagement/constants';
import { getEnumsCurrencyCode } from 'redux/reducers/emManagement/selectors';
import { tableStructure as defaultTableStructure } from './tableStructure';
import { TAnnualExchangeRateRow } from './types';
import { withRowIds } from '../../utils';
import { EExchangeRatesTabGroupLabel } from '../../constants';

/** The column whose separate filter value is sent to the API as its `currency` request field. */
const CURRENCY_FILTER_SELECTOR = 'currency';
const YEAR_FILTER_SELECTOR = 'year';
export const AnnualExchangeRates: FC = () => {
	const dispatch = useDispatch();
	const { search } = useLocation();

	const [currentTableStructure, setCurrentTableStructure] = useState(defaultTableStructure);

	const list = useSelector(getAnnualExchangeRateList);
	const listLoading = useSelector(getAnnualExchangeRateListLoading);
	const currencyCode = useSelector(getEnumsCurrencyCode);

	useEffect(() => {
		dispatch(getEmEnumsRequest({ enumName: EEmEnums.CURRENCY_CODE }));
	}, [dispatch]);

	const params = useMemo(() => queryString.parse(search), [search]);

	const currencyFilter = getColumnFilterValue(
		defaultTableStructure.find((column) => column.selector === CURRENCY_FILTER_SELECTOR),
		params,
	);

	const yearFilter = getColumnFilterValue(
		defaultTableStructure.find((column) => column.selector === YEAR_FILTER_SELECTOR),
		params,
	);

	const loadList = useCallback(() => {
		const filters: TFilters = [];
		if (currencyFilter) {
			filters.push({ fieldName: CURRENCY_FILTER_SELECTOR, value: currencyFilter, operator: '=' });
		}

		if (yearFilter) {
			filters.push({ fieldName: YEAR_FILTER_SELECTOR, value: yearFilter, operator: '=' });
		}

		const orderBy: TOrderBy[] = [];
		if (params?.sortIndex) {
			orderBy.push({
				fieldName: defaultTableStructure[+params.sortIndex].selector as string,
				direction:
					String(params.sortDirection) === 'desc' ? ESortDirection.DESC : ESortDirection.ASC,
			});
		}

		dispatch(
			getAnnualExchangeRateListRequest({
				pageNumber: params.page ? +params.page : 1,
				pageSize: PAGE_SIZE,
				searchString: String(params?.search ?? ''),
				orderBy,
				filters: filters,
			}),
		);
	}, [currencyFilter, dispatch, params, yearFilter]);

	useEffect(() => {
		loadList();
	}, [loadList]);

	const currencyOptions = useMemo<TTableStructureFilter>(
		() =>
			(currencyCode ?? [])
				.filter((item) => item.value.length === 3)
				.map((item) => [item.value, item.displayName]),
		[currencyCode],
	);

	const yearOptions = useMemo<TTableStructureFilter>(() => {
		const currentYear = new Date().getFullYear();
		const options: TTableStructureFilter = [];
		for (let year = 2020; year <= currentYear; year += 1) {
			const value = String(year);
			options.push([value, value]);
		}
		return options;
	}, []);

	const structure = useMemo(
		() =>
			currentTableStructure.map((column) =>
				column.selector === CURRENCY_FILTER_SELECTOR
					? { ...column, filter: currencyOptions }
					: column.selector === YEAR_FILTER_SELECTOR
						? { ...column, filter: yearOptions }
						: column,
			),
		[currencyOptions, currentTableStructure, yearOptions],
	);

	/** Keeps only the visibility flags, so the columns stay free of the derived filter options. */
	const handleTableStructureChange = useCallback(
		(newTableStructure: TTableStructure<keyof TAnnualExchangeRateRow>[]) => {
			setCurrentTableStructure((previous) =>
				previous.map((column) => {
					const changed = newTableStructure.find((item) => item.selector === column.selector);
					return changed ? { ...column, show: changed.show } : column;
				}),
			);
		},
		[],
	);

	const items = useMemo(() => withRowIds(list), [list]);

	return (
		<EasTableWithFilter<TAnnualExchangeRateRow>
			tableStructure={structure}
			items={items}
			itemsIsLoading={listLoading}
			handleAction={() => undefined}
			onTableStructureChange={handleTableStructureChange}
		>
			<Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
				<Typography variant="body1" color="primary">
					Resources / Reference Tables / Exchange Rates /{' '}
					<b>{EExchangeRatesTabGroupLabel.ANNUAL_EXCHANGE_RATES}</b>
				</Typography>
			</Box>
		</EasTableWithFilter>
	);
};
